import { Fragment, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import * as companyApi from '../api/Company';
import { ICompanyAndBilling, ICompanyBilling, IUserProfile } from '../util/DataModelInterface'
import { Spinner } from '../components/Spinner';
import { ToastWrap } from '../components/ToastWrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { ListGroup } from 'react-bootstrap';
import { getCodeList } from 'country-list';
import useToastHandler from '../hooks/useToastHandler';

export const CompanyInfo = () => {
    const intl = useIntl();
    const navigate = useNavigate();
    const { state } = useLocation();

    const [company, ] = useState(state.company as ICompanyAndBilling);
    const [users, setUsers] = useState([] as IUserProfile[]);
    const [language, setLanguage] = useState('');
    const [countries, setCountries] = useState({} as {[name: string]: string});
    const [saving, setSaving] = useState(false);
    const [expandDetails, setExpandDetails] = useState(false);
    const [expandInvoicing, setExpandInvoicing] = useState(false);
    const [expandMembership, setExpandMembership] = useState(false);
    const [expandUsers, setExpandUsers] = useState(false);
    const [changed, setChanged] = useState(false);
    const [toastActive, setToastActive] = useState(false);
    const [toastHeader, setToastHeader] = useState("");
    const [toastText, setToastText] = useState("");
    const [toastType, setToastType] = useState("");
    const { addToast } = useToastHandler();

    const [companyBillingState, setCompanyBillingState] = useState({} as ICompanyBilling);

    useEffect(() => {
      let countryNames = getCodeList();
      // console.log("countryNames", countryNames);
      setCountries(countryNames);
    }, []);

    useEffect(() => {
      if (company?.id) {
        companyApi.getCompanyBilling(parseInt(company.id)).then((data: any) => {
            if (data?.error) {
              addToast(data.error, 'companies', 'fetch');
            }
            else if (data) {
              setCompanyBillingState(data);
              setLanguage(data.lang);
            }
        }).finally(() => {
        });
      }
      else {
        setCompanyBillingState({
          // company_id: undefined,
          name: "",
          name2: "",
          name3: "",
          address: "",
          postal_code: "",
          city: "",
          country: "",
          reference:  "",
          vat_rate: "1",
          payment_term: "15",
          type: "1",
          email: "",
          einvoice_operator: "",
          einvoice_id: "",
          ledger_query: "",
          period: "1",
          lang: "fi"
        });
      }
    }, [company?.id, addToast]);


    useEffect(() => {
      if (company?.users?.length) {
        let ids = company.users.join();
        companyApi.getCompanyUsers(ids).then((data: any) => {
            if (data?.error) {
              addToast(data.error, 'companies', 'fetch');
            } else if (data) {
              setUsers(data);
            }
        }).finally(() => {
        });
      }
    }, [company, addToast]);

    useEffect(() => {
      if(companyBillingState.lang === language)
        setChanged(false);
      else setChanged(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [language]);

    const saveCompany = async () => {

      setSaving(true);
      setChanged(false);

      let resp = await companyApi.saveCompany({
        ...company,
        billing: {
          ...company.billing,
          lang: language
        }
      });
      if (resp?.error) {
        addToast(resp.error, 'companies', 'modify');
      } else {
        setToastHeader(intl.formatMessage({id: 'companies.saved'}));
        setToastText("");
        setToastType('success');
        setToastActive(true);
      }
      setSaving(false);
    }

    const getUserItem = (user: IUserProfile, ind: number) => {
      return (
        <Fragment key={ind}>
        { user ?
          <ListGroup.Item className="d-flex flex-row align-items-center">
            <div className="company">
              <span className="title">
                  { user.name }
                <span className="ms-4">
                  { user.user_name }
                </span>
              </span>
            </div>
          </ListGroup.Item>
          : null }
        </Fragment>
      );
    }

    return (
      <>
        <Spinner active={saving} />
        <ToastWrap active={toastActive} heading={toastHeader} text={toastText} type={toastType} setActive={(active: boolean) => setToastActive(active)} />
        <div className="action-buttons py-4 d-flex flex-row align-items-start">
            <div className="page-header me-4">
              <h1>{company.name}</h1>
            </div>

            <div className="ms-auto d-flex flex-row align-items-center">
              <button type="button" className="btn btn-secondary" onClick={() => { navigate(-1); }}>
                <i className="fa fa-close me-2"></i>
                <FormattedMessage id="button.close"/>
              </button>
              <button type="button" className="btn btn-primary btn-save" disabled={!changed}
                onClick={saveCompany}>
                <i className="fa fa-save me-2"></i>
                <FormattedMessage id="button.save"/>
              </button>

            </div>

          </div>

          <div className="controls d-flex justify-content-end my-3">
            <button className="btn btn-link p-0"
              style={ !(expandDetails && expandInvoicing && expandMembership && expandUsers) ? {} : {display: 'none'}}
              onClick={ () => { setExpandUsers(true); setExpandMembership(true); setExpandInvoicing(true); setExpandDetails(true); }}>
              <FormattedMessage id="button.expandall" />
              <i aria-hidden="true" className="fa fa-chevron-down ms-2"></i>
            </button>
            <button className="btn btn-link p-0"
              style={ expandDetails && expandInvoicing && expandMembership && expandUsers ? {} : {display: 'none'}}
              onClick={ () => { setExpandUsers(false); setExpandMembership(false); setExpandInvoicing(false); setExpandDetails(false); }}>
              <FormattedMessage id="button.collapseall" />
              <i aria-hidden="true" className="fa fa-chevron-up ms-2"></i></button>
          </div>

          <details id="details" open={expandDetails}>
            <summary onClick={ (e) => { setExpandDetails(expandDetails => !expandDetails); e.preventDefault(); }}>
              <FormattedMessage id="companies.details"/>
            </summary>
            <div>

              <p className="details-info">
                <span className="key"><FormattedMessage id="companies.name"/></span>
                <span className="value">{company.name}</span>
              </p>

              <p className="details-info">
                <span className="key"><FormattedMessage id="companies.bid"/></span>
                <span className="value">{company.business_id}</span>
              </p>

              <h2><FormattedMessage id="companies.contactinfo"/></h2>

              <p className="details-info">
                <span className="key"><FormattedMessage id="companies.address"/></span>
                <span className="value">{company.address}</span>
              </p>

              <p className="details-info">
                <span className="key"><FormattedMessage id="companies.postal_code"/></span>
                <span className="value">{company.postal_code}</span>
              </p>

              <p className="details-info">
                <span className="key"><FormattedMessage id="companies.city"/></span>
                <span className="value">{company.city}</span>
              </p>

              <p className="details-info">
                <span className="key"><FormattedMessage id="companies.country"/></span>
                <span className="value">{ countries[company?.country?.toLowerCase()] ? 
                  countries[company?.country?.toLowerCase()] : company?.country }</span>
              </p>

              <p className="details-info">
                <span className="key"><FormattedMessage id="companies.phone"/></span>
                <span className="value">{company.phone}</span>
              </p>

              <p className="details-info">
                <span className="key"><FormattedMessage id="companies.www"/></span>
                <span className="value">{company.www}</span>
              </p>

            </div>
          </details>

          <details id="invoicing" open={expandInvoicing}>
            <summary onClick={ (e) => { setExpandInvoicing(expandInvoicing => !expandInvoicing); e.preventDefault(); }}>
              <FormattedMessage id="companies.invoicing"/>
            </summary>
            <div>

              <h2><FormattedMessage id="companies.billingaddress"/></h2>

              <p className="details-info">
                <span className="key"><FormattedMessage id="companies.name"/></span>
                <span className="value">{companyBillingState.name}</span>
              </p>
              <p className="details-info">
                <span className="value">{companyBillingState.name2}</span>
              </p>
              <p className="details-info">
                <span className="value">{companyBillingState.name3}</span>
              </p>

              <p className="details-info">
                <span className="key"><FormattedMessage id="companies.address"/></span>
                <span className="value">{companyBillingState.address}</span>
              </p>

              <p className="details-info">
                <span className="key"><FormattedMessage id="companies.postal_code"/></span>
                <span className="value">{companyBillingState.postal_code}</span>
              </p>

              <p className="details-info">
                <span className="key"><FormattedMessage id="companies.city"/></span>
                <span className="value">{companyBillingState.city}</span>
              </p>

              <p className="details-info">
                <span className="key"><FormattedMessage id="companies.country"/></span>
                <span className="value">{countries[companyBillingState?.country?.toLowerCase()] ? 
                  countries[companyBillingState?.country?.toLowerCase()] : companyBillingState?.country}</span>
              </p>

              <h2><FormattedMessage id="companies.invoicing"/></h2>

              <fieldset className="form-group radiobutton-group">
                <legend className="form-label"><FormattedMessage id="companies.billinglanguage"/></legend>

                {/* <div aria-atomic="true" aria-live="polite"><div className="field-error"><i className="fa fa-warning me-2"></i>Pakollinen tieto</div></div> */}
                <div className="radiogroup form-check">
                  <label className="form-check-label" htmlFor="lang1">
                    <input type="radio" className="form-check-input" id="lang1"
                      name="lang1" value="fi" onChange={() => setLanguage("fi")} checked={language === "fi"}/>
                    <span className="label-text"><FormattedMessage id="language.finnish"/></span>
                  </label>
                </div>
                <div className="radiogroup form-check">
                  <label className="form-check-label" htmlFor="lang2">
                    <input type="radio" className="form-check-input" id="lang2"
                      name="lang2" value="se" onChange={() => setLanguage("se")} checked={language === "se"}/>
                    <span className="label-text"><FormattedMessage id="language.swedish"/></span>
                  </label>
                </div>
                <div className="radiogroup form-check">
                  <label className="form-check-label" htmlFor="lang3">
                    <input type="radio" className="form-check-input" id="lang3"
                      name="lang3" value="en" onChange={() => setLanguage("en")} checked={language === "en"}/>
                    <span className="label-text"><FormattedMessage id="language.english"/></span>
                  </label>
                </div>

              </fieldset>

                {/* Coming later... */}
                {/* <p className="details-info">
                  <span className="key"><FormattedMessage id="companies.reportingperiod"/></span>
                  <span className="value">{companyBillingState.period}</span>
                </p> */}

              <p className="details-info">
                <span className="key"><FormattedMessage id="companies.vatrate"/></span>
                <span className="value">{
                  companyBillingState.vat_rate === "1" ? "24%" : "0%"}</span>
              </p>

              <p className="details-info">
                <span className="key"><FormattedMessage id="companies.paymentterm"/></span>
                <span className="value">{companyBillingState.payment_term} <FormattedMessage id="companies.paymentterm.day"/></span>
              </p>

              <p className="details-info">
                <span className="key"><FormattedMessage id="companies.reference"/></span>
                <span className="value">{companyBillingState.reference}</span>
              </p>

              <p className="details-info">
                <span className="key"><FormattedMessage id="companies.type"/></span>
                <span className="value">{
                  companyBillingState.type === "1" ? intl.formatMessage({id: 'companies.type.post'}) :
                  companyBillingState.type === "2" ? intl.formatMessage({id: 'companies.type.email'}) :
                  intl.formatMessage({id: 'companies.type.online'})
                }</span>
              </p>

              <p className="details-info">
                <span className="key"><FormattedMessage id="companies.billingemail"/></span>
                <span className="value">{companyBillingState.email}</span>
              </p>

              <p className="details-info">
                <span className="key"><FormattedMessage id="companies.onlineid"/></span>
                <span className="value">{companyBillingState.einvoice_id}</span>
              </p>

              <p className="details-info">
                <span className="key"><FormattedMessage id="companies.operator"/></span>
                <span className="value">{companyBillingState.einvoice_operator}</span>
              </p>

            </div>
          </details>

          <details id="users" open={expandUsers}>
            <summary onClick={ (e) => { setExpandUsers(expandUsers => !expandUsers); e.preventDefault(); }}>
              <FormattedMessage id="companies.addedusers"/>
            </summary>
            <div>
              {users.length > 0 &&
              <div className="list-group list-group-flush">
                {users.map((u, ind) =>
                  getUserItem(u, ind)
                )}
              </div>
              }
            </div>
          </details>

      </>
    );
};